import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SEARCHMODE_PARAMNAME } from '../recht.constants';
import { GbrechtService, RechtMasterListEntry } from '../../../core/gb-api';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { CapAlertService } from '../../../core/extension/exceptions/cap-alert.service';
import { keysToTranslate } from 'i18n/keys-to-translate';
import { Location, NgTemplateOutlet } from '@angular/common';
import { ComparatorService } from '../../../shared/services/comparator-service';
import { ComponentLocationStrategy } from '../../../shared/services/location-strategy/component-location-strategy';
import { RechteSuchergebnisLocationStrategy } from '../../../shared/services/location-strategy/recht-suchergebnis-location-strategy';
import { OrderByRechteIdPipe } from '../../../shared/pipes/order/order-by-rechte-id.pipe';
import { getQueryParams } from '../recht.functions';
import { LinkComponent, NavTarget } from '../../../shared/components/link/link.component';
import { ListLoaderPreloaded } from '../../../shared/components/list-loader/list-loader-preloaded';
import { TableSortModel } from '../../../shared/tablesort/tableSortModel';
import { SearchStateService } from '../../../shared/services/search-state.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { RechteAuszugSettingsComponent } from '../../../shared/components/auszug/settings/rechte-auszug-settings.component';
import { GbPrintService } from '../../../core/services/app-print.service';
import { ContentHeaderComponent } from '../../../shared/components/content-header/content-header.component';
import { TitleComponent } from '../../../shared/components/title/title.component';
import { TitleFunctionbarComponent } from '../../../shared/components/title-functionbar/title-functionbar.component';
import { FunctionbarSearchresultButtonsComponent } from '../../../shared/components/title-functionbar/functionbar-searchresult-buttons/functionbar-searchresult-buttons.component';
import { ListLoaderWrapperComponent } from '../../../shared/components/list-loader-wrapper/list-loader-wrapper.component';
import { ColumnHeaderComponent } from '../../../shared/tablesort/sort-icon/column-header.component';
import { RechtListBeschreibungComponent } from '../../../shared/components/recht-list-beschreibung/recht-list-beschreibung.component';
import { KompaktBelegComponent } from '../../../shared/kompaktdarstellung/beleg/kompakt-beleg.component';
import { KompaktTimepointComponent } from '../../../shared/kompaktdarstellung/timepoint/kompakt-timepoint.component';
import { AuszugComponent } from '../../../shared/components/auszug/auszug.component';
import { FormatRechteIdPipe } from '../../../shared/pipes/format/format-rechte-id.pipe';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'cap-recht-list',
  templateUrl: './recht-list.component.html',
  styleUrls: ['./recht-list.component.scss'],
  providers: [{ provide: ComponentLocationStrategy, useClass: RechteSuchergebnisLocationStrategy }],
  imports: [
    ContentHeaderComponent,
    TitleComponent,
    TitleFunctionbarComponent,
    FunctionbarSearchresultButtonsComponent,
    ListLoaderWrapperComponent,
    NgTemplateOutlet,
    ColumnHeaderComponent,
    LinkComponent,
    RechtListBeschreibungComponent,
    KompaktBelegComponent,
    KompaktTimepointComponent,
    AuszugComponent,
    TranslatePipe,
    FormatRechteIdPipe
  ]
})
export class RechtListComponent implements OnInit, OnDestroy {
  navTargets = NavTarget;

  tableSortModel = new TableSortModel<RechtMasterListEntry>('table', this.comparator);
  listloader: ListLoaderPreloaded<RechtMasterListEntry>;
  pageSize = 15;
  showAuszugSpalte;
  rechteIds = [];
  allCheckboxesSelected = false;

  private subscription: Subscription;

  constructor(
    private gbRechtService: GbrechtService,
    private stateService: SearchStateService,
    protected router: Router,
    private route: ActivatedRoute,
    private translate: TranslateService,
    private capAlertService: CapAlertService,
    private location: Location,
    protected comparator: ComparatorService,
    private orderByRechteIdPipe: OrderByRechteIdPipe,
    private modalService: NgbModal,
    public gbPrintService: GbPrintService
  ) {
    keysToTranslate<string>([
      'Beleg',
      'RechteId',
      'Beschreibung',
      'KeineDatenVorhanden',
      'Loading',
      'Zurück',
      'DetailsZuArtAnzeigen',
      'detailsGeschäft',
      'illegalObjectState',
      'stichwortNichtVorhanden',
      'searchZuVieleSuchkriterien'
    ]);
    this.tableSortModel
      .addColumn('rechteid', (a, b) =>
        this.comparator.rechtIdVersionComparator(
          a.rechteId,
          b.rechteId,
          a.version.histInfo,
          b.version.histInfo,
          a.version.erstellzeitpunkt,
          b.version.erstellzeitpunkt
        )
      )
      .switchSortDirection();
    this.tableSortModel.addColumn('art', (a, b) => this.comparator.translatedStringComparator(a.art, b.art));
    this.tableSortModel.addColumn('beleg', (a, b) => this.comparator.belegComparator(a.version.beleg, b.version.beleg));
  }

  ngOnInit(): void {
    const searchMode = this.route.snapshot.params[SEARCHMODE_PARAMNAME];
    const req = this.route.snapshot.queryParams['request'];
    if (req) {
      const request = JSON.parse(req);
      this.stateService.lastSearchMode = searchMode;
      this.subscription = this.getRechtListSubscription(request)[searchMode].subscribe({
        next: (response) => {
          if (response.recordsFound === 0) {
            this.capAlertService.info(this.translate.instant('KeineResultateGefunden'), undefined, true);
            this.navigateBackToSearch();
          }
          this.listloader = new ListLoaderPreloaded<RechtMasterListEntry>(
            this.pageSize,
            1,
            this.orderByRechteIdPipe.transform(response.results)
          );
          this.listloader.totalItemsFound = response.recordsFound;

          this.listloader.applySortModel(this.tableSortModel);
          this.calculateShowAuszugSpalte();
        },
        error: (error: HttpErrorResponse) => {
          if (error.status === 400) {
            this.capAlertService.info(this.translate.instant('EingabeMussValideEreidSein'), undefined, true);
            this.navigateBackToSearch();
          }
          if (error.status === 403) {
            this.capAlertService.info(this.translate.instant('KeineResultateGefunden'), undefined, true);
            this.navigateBackToSearch();
          }
        }
      });
    }
  }

  navigateBackToSearch() {
    this.location.back();
  }

  getQueryParams(version: RechtMasterListEntry) {
    return getQueryParams(version);
  }

  private calculateShowAuszugSpalte() {
    const keineZeileHatPdfRtf = this.listloader.items.every((item) => !item.version.printPDFpossible && !item.version.printRTFpossible);
    this.showAuszugSpalte = !keineZeileHatPdfRtf;
  }

  private getRechtListSubscription(request) {
    return {
      searchStandard: this.gbRechtService.searchRechteId(request),
      searchErweitert: this.gbRechtService.searchRechtErweitert(request),
      searchFachId: this.gbRechtService.searchRechtEreid(request)
    };
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  getZip() {
    this.selectGrundstueckSettings(this.rechteIds);
  }

  selectGrundstueckSettings(list) {
    const modalRef = this.modalService.open(RechteAuszugSettingsComponent);
    modalRef.componentInstance.hasToZip = true;
    modalRef.componentInstance.ids = list;
    modalRef.componentInstance.indexNumber = 0;
  }

  selectAllCheckboxes(items: Array<RechtMasterListEntry>, event: any) {
    this.allCheckboxesSelected = !this.allCheckboxesSelected;

    for (let i = 0; i < items.length; i++) {
      if (items[i].art !== 'PFANDRECHT') {
        this.addCheckedRecht(items[i].version.id.id, event);
      }
    }
  }

  addCheckedRecht(id: string, event: any) {
    const rechteId = id;
    if (event.checked) {
      if (this.rechteIds.includes(rechteId)) {
        return;
      }
      this.rechteIds.push(rechteId);
    } else {
      this.rechteIds = this.rechteIds.filter((id) => id !== rechteId);
    }
  }

  removeAllCheckboxes() {
    const selectAllCheckbox = document.querySelector('#selectAllCheckbox') as HTMLInputElement;
    if (selectAllCheckbox) {
      selectAllCheckbox.checked = false;
    }
    this.rechteIds = [];
  }
}
